import * as FilePond from "filepond"
import FilePondPluginImagePreview from "filepond-plugin-image-preview"
import FilePondPluginFileMetadata from "filepond-plugin-file-metadata"
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type"
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size"
import { transferFileElementOutOfPond } from "../../views/creator/products/version_form"
import { displayFlashMessage } from "../../util/methods"
import { openImageCropper } from "./image_cropper"
import { presignAndUpload } from "../s3_upload/presign_upload"

// Shared rules for product images: JPG/PNG/WEBP only, 5MB. The exact 2:1 ratio and
// the 1200x600–4000x2000 bounds are handled by the in-app cropper (see
// cropToTwoToOne below) rather than by rejecting off-ratio images.
const PRODUCT_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"]
const PRODUCT_IMAGE_RULES = {
  maxFileSize: "5MB",
  acceptedFileTypes: PRODUCT_IMAGE_TYPES,
  allowImagePreview: true,
  allowPaste: false,
}

const MIN_IMAGE_WIDTH = 1200
const MIN_IMAGE_HEIGHT = 600
const MAX_IMAGE_WIDTH = 4000
const MAX_IMAGE_HEIGHT = 2000

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = function () {
      URL.revokeObjectURL(url)
      resolve({ width: img.width, height: img.height })
    }
    img.onerror = function () {
      URL.revokeObjectURL(url)
      reject(new Error("Could not read image dimensions"))
    }
    img.src = url
  })
}

function isTwoToOne(width, height) {
  return Math.abs(width / height - 2) < 0.01
}

// FilePond beforeAddFile hook for product images. Already-valid 2:1 images upload
// as-is; off-ratio (or oversized) images open the cropper so the creator can crop
// to an exact 2:1. The cropped file is re-added to the same pond. `pond` is the
// FilePond instance, captured so the cropped result lands back in the right input.
async function cropToTwoToOne(item, pond) {
  let width
  let height
  try {
    ;({ width, height } = await getImageDimensions(item.file))
  } catch (e) {
    displayFlashMessage(
      "danger",
      "Could not read this image. Please try a different file."
    )
    return false
  }

  // Already an exact 2:1 within bounds — upload directly, no cropper needed.
  if (
    isTwoToOne(width, height) &&
    width <= MAX_IMAGE_WIDTH &&
    height <= MAX_IMAGE_HEIGHT
  ) {
    return true
  }

  // Too small to crop a valid 2:1 region out of.
  if (width < MIN_IMAGE_WIDTH || height < MIN_IMAGE_HEIGHT) {
    displayFlashMessage(
      "danger",
      "Image is too small — it must be at least 1200×600 to crop to 2:1."
    )
    return false
  }

  // Open the cropper; add the cropped 2:1 result back to this pond when done.
  openImageCropper(item.file).then(function (cropped) {
    if (cropped) pond.addFile(cropped)
  })
  return false
}

// Creator application work sample must be a .blend file or an add-on .zip.
// .blend has no reliable MIME type, so validate by extension.
const APPLICATION_SAMPLE_EXTENSIONS = ["blend", "zip"]
function validateSampleFile(item) {
  const ext = item.file.name.split(".").pop().toLowerCase()
  if (APPLICATION_SAMPLE_EXTENSIONS.includes(ext)) return true
  displayFlashMessage(
    "danger",
    "Please upload a .blend file or your add-on as a .zip."
  )
  return false
}

$(document).on("turbolinks:load", function () {
  // FilePond setup
  FilePond.registerPlugin(FilePondPluginImagePreview)
  FilePond.registerPlugin(FilePondPluginFileMetadata)
  FilePond.registerPlugin(FilePondPluginFileValidateType)
  FilePond.registerPlugin(FilePondPluginFileValidateSize)

  var $featuredProductImageInputs = $(".js-imageInputFeatured")
  $featuredProductImageInputs.each(function (index, el) {
    var $el = $(el)
    var pond = FilePond.create(el, {
      ...PRODUCT_IMAGE_RULES,
      maxFiles: 1,
      allowMultiple: false,
      beforeAddFile: (item) => cropToTwoToOne(item, pond),
      server: {
        load: loadFile,
        process: processProductFeaturedImage,
        revert: removeProductFeaturedImage,
        remove: removeProductFeaturedImage,
      },
      files: $el.data("files"),
      onupdatefiles: function (files) {
        $(el)
          .closest(".js-featured-image-wrapper")
          .toggleClass("has-file", files.length > 0)
      },
    })
  })

  var $couponImageInputs = $(".js-imageCouponInputFeatured")
  $couponImageInputs.each(function (index, el) {
    var $el = $(el)
    FilePond.create(el, {
      maxFiles: 1,
      maxFileSize: "5MB",
      acceptedFileTypes: ["image/png", "image/jpeg", "image/gif"],
      allowMultiple: false,
      allowImagePreview: true,
      allowPaste: false,
      server: {
        load: loadFile,
        process: processCouponFeaturedImage,
        revert: removeCouponFeaturedImage,
        remove: removeCouponFeaturedImage,
      },
      files: $el.data("files"),
    })
  })

  var $multiImageInputs = $(".js-imageInputMulti")
  var $addToTop = $("#js-image-add-to-top")
  $multiImageInputs.each(function (index, el) {
    var $el = $(el)
    var imageInsertLocation = $addToTop.val() === "true" ? "before" : "after"
    var pond = FilePond.create(el, {
      ...PRODUCT_IMAGE_RULES,
      allowMultiple: true,
      imagePreviewHeight: 200,
      itemInsertLocation: imageInsertLocation,
      allowReorder: true,
      beforeAddFile: (item) => cropToTwoToOne(item, pond),
      server: {
        load: loadFile,
        process: processImageFile,
        revert: removeImage,
        remove: removeImage,
      },
      files: $el.data("files"),
      onprocessfile: setImageOrder,
      onreorderfiles: reorderProductImages,
    })

    function setImageOrder() {
      reorderProductImages(pond.getFiles())
    }
  })

  var $bundleImageInputs = $(".js-imageInputBundle")
  $bundleImageInputs.each(function (index, el) {
    var $el = $(el)
    var pond = FilePond.create(el, {
      ...PRODUCT_IMAGE_RULES,
      maxFiles: 1,
      allowMultiple: false,
      beforeAddFile: (item) => cropToTwoToOne(item, pond),
      server: {
        load: loadFile,
        process: processBundleImage,
        revert: removeBundleImage,
        remove: removeBundleImage,
      },
      files: $el.data("files"),
    })
  })

  var $downloadInputs = $(".js-fileInputDownload")
  $downloadInputs.each(function () {
    FilePond.create(this, {
      allowMultiple: true,
      allowRevert: true,
      allowRemove: true,
      allowPaste: false,
      server: {
        process: processDownloadFile,
        revert: removeDownloadFile,
        remove: removeDownloadFile,
      },
    })
  })

  var $versionDownloadInputs = $(".js-versionFileInputDownload")
  $versionDownloadInputs.each(function () {
    FilePond.create(this, {
      allowMultiple: true,
      allowRevert: false,
      allowRemove: false,
      allowPaste: false,
      server: {
        process: processVersionDownloadFile,
      },
    })
  })

  var $avatarInputs = $(".js-imageInputAvatar")
  $avatarInputs.each(function (index, el) {
    var $el = $(el)
    FilePond.create(el, {
      maxFileSize: "5MB",
      acceptedFileTypes: ["image/*"],
      maxFiles: 1,
      allowMultiple: false,
      allowImagePreview: true,
      server: {
        load: loadFile,
        process: processAvatarImage,
        revert: removeAvatarImage,
        remove: removeAvatarImage,
      },
      files: $el.data("files"),
    })
  })

  var $messageInputs = $(".js-fileInputMessage")
  $messageInputs.each(function () {
    FilePond.create(this, {
      maxFileSize: "5MB",
      maxFiles: 1,
      allowMultiple: false,
      allowRevert: false,
      allowRemove: false,
      server: {
        process: processMessageFile,
      },
    })
  })

  var $applicationSampleInputs = $(".js-fileInputApplicationSample")
  $applicationSampleInputs.each(function () {
    FilePond.create(this, {
      maxFiles: 1,
      maxFileSize: "200MB",
      allowMultiple: false,
      allowRevert: true,
      allowRemove: true,
      allowPaste: false,
      beforeAddFile: validateSampleFile,
      server: {
        process: processApplicationSampleFile,
        revert: removeApplicationSampleFile,
        remove: removeApplicationSampleFile,
      },
    })
  })

  var $postImageInputs = $(".js-imageInputPost")
  $postImageInputs.each(function (index, el) {
    var $el = $(el)
    FilePond.create(el, {
      maxFiles: 1,
      maxFileSize: "5MB",
      acceptedFileTypes: ["image/png", "image/jpeg", "image/gif"],
      allowMultiple: false,
      allowImagePreview: true,
      server: {
        load: loadFile,
        process: processPostImage,
        revert: removePostImage,
        remove: removePostImage,
      },
      files: $el.data("files"),
    })
  })

  var $shopBannerInputs = $(".js-imageInputBanner")
  $shopBannerInputs.each(function (index, el) {
    var $el = $(el)
    FilePond.create(el, {
      maxFiles: 1,
      maxFileSize: "5MB",
      acceptedFileTypes: ["image/png", "image/jpeg", "image/gif"],
      allowMultiple: false,
      allowImagePreview: true,
      server: {
        load: loadFile,
        process: processBannerImage,
        revert: removeBannerImage,
        remove: removeBannerImage,
      },
      files: $el.data("files"),
    })
  })
})

function loadFile(source, load) {
  $.ajax({
    url: source.split("|")[1], // [0] is the database ID
    method: "get",
    cache: false, // avoid S3 cors issues
    xhrFields: {
      responseType: "blob",
    },
    success: function (response) {
      load(response)
    },
  })
}

//
// 1. PROCESS
//
function processImageFile(fieldName, file, metadata, load, error) {
  // Begin processing image by sending a request to the server
  // Subsequent methods are handled in 2 callbacks: sendFileToS3, createProductImage
  // SetTimeout causes this to be started in a thread, which helps with animations
  setTimeout(
    getPresignParams(
      file,
      load,
      error,
      createProductImage,
      "/s3/public_params"
    ),
    0
  )
}

function processProductFeaturedImage(fieldName, file, metadata, load, error) {
  setTimeout(() => {
    getPresignParams(
      file,
      load,
      error,
      updateProductFeaturedImage,
      "/s3/public_params"
    )
  }, 0)
}

function processCouponFeaturedImage(fieldName, file, metadata, load, error) {
  setTimeout(() => {
    getPresignParams(
      file,
      load,
      error,
      updateCouponFeaturedImage,
      "/s3/public_params"
    )
  }, 0)
}

function processBundleImage(fieldName, file, metadata, load, error) {
  setTimeout(
    getPresignParams(file, load, error, updateBundleImage, "/s3/public_params"),
    0
  )
}

// Product files can be gigabytes. These two ponds go through the shared
// upload module (multipart above its threshold, single PUT below) and wire
// FilePond's progress and abort callbacks, which the image ponds above
// ignore. The returned { abort } is what FilePond calls on cancel.
function processLargeFile(file, load, error, progress, abort, afterUpload) {
  var controller = new AbortController()
  var staging = null

  presignAndUpload(file, "/s3/params", {
    signal: controller.signal,
    onProgress: function (loaded, total) {
      progress(true, loaded, total)
    },
  })
    .then(function (uploadedFileData) {
      // Cancelled after the bytes landed but before the app was told: stop
      // here rather than create a row the user no longer wants.
      if (controller.signal.aborted) {
        abort()
        return
      }
      // Keep the staging request so a cancel can abort it too; jQuery fires
      // the fail handler with status "abort" for that, and with "error",
      // "timeout" or "parsererror" when the app could not record the file.
      // The success path reports response.success == false itself.
      staging = afterUpload(file, uploadedFileData, load, error)
      if (staging && staging.fail) {
        staging.fail(function (_jqXHR, textStatus) {
          if (textStatus === "abort") {
            abort()
          } else {
            // The bytes landed but the record was not created: show an error
            // instead of leaving the item spinning forever.
            error("Could not save the file, please try again")
          }
        })
      }
    })
    .catch(function (err) {
      if (err && err.name === "AbortError") {
        abort()
      } else {
        error(uploadErrorMessage(err))
      }
    })

  return {
    abort: function () {
      controller.abort()
      if (staging && staging.abort) staging.abort()
    },
  }
}

function uploadErrorMessage(err) {
  if (err && err.code === "too_large") return "File is too large"
  if (err && err.code === "network") return "Connection lost, try again"
  return "Upload failed"
}

function processDownloadFile(
  fieldName,
  file,
  metadata,
  load,
  error,
  progress,
  abort
) {
  return processLargeFile(
    file,
    load,
    error,
    progress,
    abort,
    createProductDownload
  )
}

function processVersionDownloadFile(
  fieldName,
  file,
  metadata,
  load,
  error,
  progress,
  abort
) {
  return processLargeFile(
    file,
    load,
    error,
    progress,
    abort,
    createProductVersionDownload
  )
}

function processAvatarImage(fieldName, file, metadata, load, error) {
  setTimeout(
    getPresignParams(
      file,
      load,
      error,
      updateUserAvatarImage,
      "/s3/public_params"
    ),
    0
  )
}

function processMessageFile(fieldName, file, metadata, load, error) {
  setTimeout(getPresignParams(file, load, error, assignMessageFiles), 0)
}

function processPostImage(fieldName, file, metadata, load, error) {
  setTimeout(
    getPresignParams(file, load, error, updatePostImage, "/s3/public_params"),
    0
  )
}

function processBannerImage(fieldName, file, metadata, load, error) {
  setTimeout(
    getPresignParams(
      file,
      load,
      error,
      updateShopBannerImage,
      "/s3/public_params"
    ),
    0
  )
}

//
// 2. PRESIGN
//
// components/s3_upload/presign_upload.js is the shared, progress-aware version
// of this presign → S3 PUT → cached-upload-JSON flow; the large-file ponds
// (product downloads, version downloads) use it. The image ponds still use this
// jQuery copy. Keep the presign contract (query params, /cache/ key parsing,
// cached-JSON shape) in sync between the two.
function getPresignParams(
  file,
  load,
  error,
  callbackMethod,
  url = "/s3/params"
) {
  // Server should presign the file and return params for S3
  $.ajax({
    url: url,
    method: "get",
    data: {
      filename: file.name,
      type: file.type,
    },
    success: function (presignResponse) {
      setTimeout(
        sendFileToS3(file, presignResponse, load, error, callbackMethod),
        0
      )
    },
  })
}

//
// 3. S3 Storage
//
function sendFileToS3(file, presignResponse, load, error, callbackMethod) {
  // Presign succeeded
  var url = new URL(presignResponse["url"])

  $.ajax({
    url: url,
    method: "PUT",
    headers: presignResponse["headers"],
    data: file,
    processData: false,
    contentType: file.type,
    success: function () {
      // File has been successfully uploaded to S3!
      // Now we have to inform our server of the upload.

      var presignFileData = {
        key: url.pathname,
      }
      setTimeout(callbackMethod(file, presignFileData, load, error), 0)
    },
  })
}

//
// 4. APP HOOKUPS
//

function formatCachedUpload(file, presignFileData, storage = "cache") {
  return {
    id: presignFileData["key"].match(/^\/cache\/(.+)/)[1], // object key without cache prefix
    storage: storage,
    metadata: {
      size: file.size,
      filename: file.name,
      mime_type: file.type,
    },
  }
}

// This method takes the CSRF token from a form and submits it remotely
function ajaxUpdateForm(formSelector, requestMethod, data, successCallback) {
  var $form = $(formSelector)
  var formData = {
    _method: requestMethod,
    authenticity_token: $form.find('input[name="authenticity_token"]').val(),
  }
  return $.ajax({
    url: $form.attr("action"),
    method: "post",
    dataType: "json",
    data: {
      ...formData,
      ...data,
    },
    success: successCallback,
  })
}

function updateProductFeaturedImage(file, presignFileData, load, error) {
  // Construct uploaded file data in the format that Shrine expects
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  // Make an AJAX request to update our server with the new image
  ajaxUpdateForm(
    ".js-section-form, .edit_product",
    "patch",
    { product: { image: uploadedFileData } },
    function (response) {
      if (response["success"] == true) {
        load(uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

function updateCouponFeaturedImage(file, presignFileData, load, error) {
  var $isEditCoupon = $(".edit_coupon").length > 0

  // Construct uploaded file data in the format that Shrine expects
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  if ($isEditCoupon) {
    ajaxUpdateForm(
      ".edit_coupon",
      "patch",
      { coupon: { image: uploadedFileData } },
      function (response) {
        if (response["success"] == true) {
          load(uploadedFileData["id"])
        } else {
          error("Something went wrong")
        }
      }
    )
  }
}

function updateBundleImage(file, presignFileData, load, error) {
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  ajaxUpdateForm(
    ".js-productBundle-form",
    "patch",
    { product_bundle: { image: uploadedFileData } },
    function (response) {
      if (response["success"] == true) {
        load(uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

function createProductImage(file, presignFileData, load, error) {
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  ajaxUpdateForm(
    ".js-section-form, .edit_product",
    "patch",
    { product: { images_attributes: [{ id: null, image: uploadedFileData }] } },
    function (response) {
      // Tell FilePond to finish. last_image_id is used to associate the image
      // to the newly created ProductImage in the back end.
      if (response["success"] == true) {
        load(response["last_image_id"] + "|" + uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

function createProductDownload(file, uploadedFileData, load, error) {
  // uploadedFileData is the Shrine cached-upload JSON from presignAndUpload.
  // Make an AJAX request to update our server with the new file
  return ajaxUpdateForm(
    ".js-section-form, .edit_product",
    "patch",
    {
      product: {
        downloads_attributes: [
          { id: null, title: file.name, file: uploadedFileData },
        ],
      },
    },
    function (response) {
      // Tell FilePond to finish. Store the DB download id alongside the cache id
      // (db_id|cache_id) so the file can be removed from the pond, mirroring images.
      if (response["success"] == true) {
        load(response["last_download_id"] + "|" + uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

function removeDownloadFile(source, load, error) {
  var downloadId = source.split("|")[0]
  var $form = $(".js-section-form, .edit_product")

  // Use the dedicated Creator::DownloadsController#destroy endpoint
  // (/creator/products/:id/downloads/:download_id) rather than the product
  // update, which requires a full product param. The section form's action is
  // /creator/products/:id/edit/:section, so build the URL from the product id.
  $.ajax({
    url:
      "/creator/products/" +
      $form.data("product-id") +
      "/downloads/" +
      downloadId,
    method: "post",
    dataType: "json",
    data: {
      _method: "delete",
      authenticity_token: $form.find('input[name="authenticity_token"]').val(),
    },
    success: function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    },
    error: function () {
      error("File could not be removed")
    },
  })
}

function createProductVersionDownload(file, uploadedFileData, load, error) {
  // uploadedFileData is the Shrine cached-upload JSON from presignAndUpload.
  // Make an AJAX request to update our server with the new product file
  return ajaxUpdateForm(
    ".new_download",
    "post",
    { download: { id: null, title: file.name, file: uploadedFileData } },
    function (response) {
      // Tell FilePond to finish and create html element for response data
      if (response["success"] == true) {
        load(uploadedFileData["id"])
        var pond = FilePond.find($(".js-versionFileInputDownload")[0])

        setTimeout(function () {
          transferFileElementOutOfPond(pond, file, response["html"])
        }, 2000)
      } else {
        error("Something went wrong")
      }
    }
  )
}

function updateUserAvatarImage(file, presignFileData, load, error) {
  // Construct uploaded file data in the format that Shrine expects
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  // Make an AJAX request to update our server with the new image
  ajaxUpdateForm(
    ".edit_user",
    "patch",
    { user: { avatar: uploadedFileData } },
    function (response) {
      if (response["success"] == true) {
        load(uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

function assignMessageFiles(file, presignFileData, load) {
  // Construct uploaded file data in the format that Shrine expects
  var uploadedFileData = formatCachedUpload(file, presignFileData)
  // Put it in a hidden field to be used on form submit
  $(".js-fileInputMessage-target").val(JSON.stringify(uploadedFileData))
  load(uploadedFileData["id"])
}

// Creator application sample: the application record does not exist yet, so we
// upload to private S3 cache and stash the cached-file JSON in a hidden field
// that is submitted with the form (Shrine promotes it on save).
function processApplicationSampleFile(fieldName, file, metadata, load, error) {
  setTimeout(
    getPresignParams(file, load, error, assignApplicationSampleFile),
    0
  )
}

function assignApplicationSampleFile(file, presignFileData, load) {
  var uploadedFileData = formatCachedUpload(file, presignFileData)
  $(".js-fileInputApplicationSample-target").val(
    JSON.stringify(uploadedFileData)
  )
  load(uploadedFileData["id"])
}

function removeApplicationSampleFile(source, load) {
  $(".js-fileInputApplicationSample-target").val("")
  load()
}

function updatePostImage(file, presignFileData, load, error) {
  // Construct uploaded file data in the format that Shrine expects
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  // Make an AJAX request to update our server with the new image
  ajaxUpdateForm(
    ".edit_post",
    "patch",
    { post: { image: uploadedFileData } },
    function (response) {
      if (response["success"] == true) {
        load(uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

function updateShopBannerImage(file, presignFileData, load, error) {
  // Construct uploaded file data in the format that Shrine expects
  var uploadedFileData = formatCachedUpload(
    file,
    presignFileData,
    "public_cache"
  )

  //the controller expects a settings id, otherwise it will create a new ShopSetting
  var $settingsId = $('input[name="shop[settings_attributes][id]"]').val()

  // Make an AJAX request to update our server with the new image
  ajaxUpdateForm(
    ".edit_shop",
    "patch",
    {
      shop: {
        settings_attributes: {
          id: $settingsId,
          banner: uploadedFileData,
        },
      },
    },
    function (response) {
      if (response["success"] == true) {
        load(uploadedFileData["id"])
      } else {
        error("Something went wrong")
      }
    }
  )
}

//
// 4a. IMAGE GALLERY REORDERING
//

// This only works with 1 multiple image gallery at a time due to the global variable.
// If needed we can refactor this later to be more flexible.
var temporaryImageReorderTimeout
function reorderProductImages(filesArray) {
  // Set a timeout. After 5 seconds of no activity, let's update the server
  clearTimeout(temporaryImageReorderTimeout)
  temporaryImageReorderTimeout = setTimeout(function () {
    updateProductImageOrdinals(filesArray)
  }, 5000)
}

function updateProductImageOrdinals(filesArray) {
  var formattedData = []
  for (var i = 0; i < filesArray.length; i++) {
    formattedData.push({
      id: filesArray[i].serverId.split("|")[0],
      ordinal: i,
    })
  }

  ajaxUpdateForm(
    ".js-section-form, .edit_product",
    "patch",
    { product: { images_attributes: formattedData } },
    function () {
      // pass
    }
  )
}

//
// 5. REMOVE / REVERT
//
function removeProductFeaturedImage(source, load, error) {
  // Make an AJAX request to delete the image
  ajaxUpdateForm(
    ".js-section-form, .edit_product",
    "patch",
    { product: { remove_image: true } },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}

function removeCouponFeaturedImage(source, load, error) {
  // Make an AJAX request to delete the image
  ajaxUpdateForm(
    ".edit_coupon",
    "patch",
    { coupon: { remove_image: true } },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}

function removeImage(source, load, error) {
  var productImageID = source.split("|")[0]

  // Make an AJAX request to delete the image
  ajaxUpdateForm(
    ".js-section-form, .edit_product",
    "patch",
    { product: { destroy_product_images: [productImageID] } },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}

function removeBundleImage(source, load, error) {
  ajaxUpdateForm(
    ".js-productBundle-form",
    "patch",
    { product_bundle: { remove_image: true } },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}

function removeAvatarImage(source, load, error) {
  // Make an AJAX request to delete the image
  ajaxUpdateForm(
    ".edit_user",
    "patch",
    { user: { remove_avatar: true } },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}

function removePostImage(source, load, error) {
  // Make an AJAX request to delete the image
  ajaxUpdateForm(
    ".edit_post",
    "patch",
    { post: { remove_image: true } },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}

function removeBannerImage(source, load, error) {
  var $settingsId = $('input[name="shop[settings_attributes][id]"]').val()

  // Make an AJAX request to delete the image
  ajaxUpdateForm(
    ".edit_shop",
    "patch",
    {
      shop: {
        settings_attributes: {
          id: $settingsId,
          remove_banner: true,
        },
      },
    },
    function (response) {
      if (response["success"] == true) {
        load()
      } else {
        error("File could not be removed")
      }
    }
  )
}
